import { ArrowLeft, BookOpen, Clock, Star, Users } from 'lucide-react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Reveal } from '@/shared/motion/reveal'
import { Section, SectionHeader } from '@/shared/ui/section'
import { Button } from '@/shared/ui/button'
import { defineT, useLocale, useTranslation } from '@/shared/i18n/use-translation'
import { cn } from '@/shared/lib/cn'
import { fetchCourses } from '@/features/courses/api'
import { COURSES_FALLBACK } from '@/features/courses/data/fallback'
import { LEVEL_LABELS } from '@/features/courses/lib/format'
import type { Course as ApiCourse } from '@/features/courses/types'

const T = defineT({
  eyebrow: { ar: 'كورساتي', en: 'My Courses' },
  title: {
    ar: 'تعلّم من الصفر للاحتراف',
    en: 'Learn from zero to pro',
  },
  description: {
    ar: 'كورسات مدروسة، مشاريع حقيقية، ومجتمع داعم — مش مجرد محاضرات.',
    en: 'Curated curricula, real projects, and a supportive community — not just lectures.',
  },
  viewAll: { ar: 'كل الكورسات', en: 'All courses' },
  enrolled: { ar: 'طالب', en: 'students' },
  hours: { ar: 'ساعة', en: 'hours' },
  start: { ar: 'ابدأ الكورس', en: 'Start course' },
  hot: { ar: 'الأكثر طلباً', en: 'Most popular' },
  new: { ar: 'جديد', en: 'New' },
})

const FALLBACK_COVERS = [
  '/card-ai.webp',
  '/card-engineering.webp',
  '/card-imagery.webp',
  '/card-prompts.webp',
  '/card-teaching.webp',
  '/zatoona-camera-angles.webp',
]

export function FeaturedCourses() {
  const { t, isRtl } = useTranslation(T)
  const { locale } = useLocale()

  // The home page is the highest-traffic surface — keep stale data 30
  // minutes so a navigation back from /courses doesn't trigger another
  // network round trip.
  const { data, isLoading } = useQuery({
    queryKey: ['courses', 'list'],
    queryFn: ({ signal }) => fetchCourses(signal),
    staleTime: 30 * 60 * 1000,
  })

  const allCourses = data ?? COURSES_FALLBACK
  // Show at most 3 — featured first, then highest-rated, then newest.
  const courses = [...allCourses]
    .filter((c) => c.isPublished)
    .sort((a, b) => {
      if (a.isFeatured !== b.isFeatured) return a.isFeatured ? -1 : 1
      const ra = a.avgRating ?? 0
      const rb = b.avgRating ?? 0
      if (ra !== rb) return rb - ra
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    })
    .slice(0, 3)

  return (
    <Section id="courses" pad="md">
      <Reveal>
        <SectionHeader
          eyebrow={t('eyebrow')}
          title={t('title')}
          description={t('description')}
        />
      </Reveal>

      {isLoading ? (
        <ul role="list" className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <li
              key={i}
              className="h-[26rem] animate-pulse rounded-2xl border border-border bg-card/40"
            />
          ))}
        </ul>
      ) : (
        <div className="stagger grid gap-5 md:grid-cols-2 lg:grid-cols-3">
          {courses.map((c, i) => (
            <Reveal key={c.id} className="contents">
              <CourseTile
                course={c}
                fallbackCover={FALLBACK_COVERS[i % FALLBACK_COVERS.length]}
                t={t}
                isRtl={isRtl}
                locale={locale}
              />
            </Reveal>
          ))}
        </div>
      )}

      <Reveal>
        <div className="mt-12 flex justify-center">
          <Link to="/courses">
            <Button variant="outline" size="lg" type="button">
              {t('viewAll')}
              <ArrowLeft
                className={cn('h-4 w-4', !isRtl && 'rotate-180')}
                aria-hidden
              />
            </Button>
          </Link>
        </div>
      </Reveal>
    </Section>
  )
}

interface CourseTileProps {
  course: ApiCourse
  fallbackCover: string
  t: (key: keyof typeof T) => string
  isRtl: boolean
  locale: 'ar' | 'en'
}

function CourseTile({ course, fallbackCover, t, isRtl, locale }: CourseTileProps) {
  const cover = course.thumbnail || fallbackCover
  const levelLabel = LEVEL_LABELS[course.level]?.[locale] ?? course.level
  const hours = course.duration ? Math.round(course.duration / 60) : null
  const students = course.studentsCount ?? 0
  const rating = course.avgRating ?? 0
  const badge: 'hot' | 'new' | undefined = course.isFeatured
    ? 'hot'
    : (() => {
        if (!course.createdAt) return undefined
        const ageMs = Date.now() - new Date(course.createdAt).getTime()
        return ageMs < 1000 * 60 * 60 * 24 * 60 ? 'new' : undefined
      })()

  return (
    <Link
      to={`/course/${course.slug}`}
      className={cn(
        'group relative isolate overflow-hidden rounded-2xl border border-border bg-card',
        'card-hover',
        'flex flex-col',
      )}
    >
      {/* Cover */}
      <div className="relative aspect-[16/10] overflow-hidden bg-secondary">
        <img
          src={cover}
          alt={course.title}
          width={640}
          height={400}
          loading="lazy"
          decoding="async"
          className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.04]"
        />
        <div
          className="absolute inset-0 bg-gradient-to-t from-card via-card/30 to-transparent"
          aria-hidden
        />
        {badge && (
          <span
            className={cn(
              'absolute top-3 end-3 rounded-full px-2.5 py-1 text-xs font-semibold backdrop-blur-md',
              badge === 'hot'
                ? 'bg-primary text-primary-foreground'
                : 'bg-success text-success-foreground',
            )}
          >
            {badge === 'hot' ? t('hot') : t('new')}
          </span>
        )}
        <div className="absolute bottom-3 start-3 inline-flex items-center gap-1.5 rounded-full bg-background/70 px-2.5 py-1 text-xs font-medium text-foreground backdrop-blur-md">
          <BookOpen className="h-3 w-3" aria-hidden />
          {levelLabel}
        </div>
      </div>

      {/* Body */}
      <div className="flex flex-1 flex-col p-6">
        <h3 className="font-display text-xl font-bold leading-snug tracking-tight text-foreground transition-colors group-hover:text-primary">
          {course.title}
        </h3>

        <ul
          role="list"
          className="mt-5 grid grid-cols-3 gap-3 text-xs text-muted-foreground"
        >
          <li className="inline-flex items-center gap-1.5">
            <Star className="h-3.5 w-3.5 text-primary" aria-hidden />
            <span className="font-semibold tabular-nums text-foreground">
              {rating > 0 ? rating.toFixed(1) : '—'}
            </span>
          </li>
          <li className="inline-flex items-center gap-1.5">
            <Users className="h-3.5 w-3.5" aria-hidden />
            <span className="tabular-nums">
              {students.toLocaleString(locale === 'ar' ? 'ar-EG' : 'en-US')}{' '}
              {t('enrolled')}
            </span>
          </li>
          <li className="inline-flex items-center gap-1.5">
            <Clock className="h-3.5 w-3.5" aria-hidden />
            <span className="tabular-nums">
              {hours ?? '—'} {t('hours')}
            </span>
          </li>
        </ul>

        <div className="mt-6 inline-flex items-center gap-1.5 text-sm font-medium text-primary">
          {t('start')}
          <ArrowLeft
            className={cn(
              'h-4 w-4 transition-transform duration-300',
              isRtl
                ? 'group-hover:-translate-x-1'
                : 'rotate-180 group-hover:translate-x-1',
            )}
            aria-hidden
          />
        </div>
      </div>
    </Link>
  )
}
